import { Component, ElementRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { NotificationInboxStore } from '../../../core/store/notification-inbox.store';
import { PusherService } from '../../../core/notifications/pusher.service';
import { NotificationPanelComponent } from '../notification-panel/notification-panel.component';

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [NotificationPanelComponent],
  template: `
    <div class="notification-bell-wrapper">
      <button class="bell-btn" (click)="store.togglePanel()" aria-label="Notifications">
        <i class="pi pi-bell"></i>
        @if (store.unreadCount() > 0) {
          <span class="badge">{{ store.unreadCount() > 99 ? '99+' : store.unreadCount() }}</span>
        }
      </button>
      @if (store.panelOpen()) {
        <app-notification-panel />
      }
    </div>
  `,
  styles: [`
    .notification-bell-wrapper {
      position: fixed;
      top: 8px;
      right: 41px;
      z-index: 900;
      display: inline-flex;
      align-items: center;
    }
    .bell-btn {
      position: relative;
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      border-radius: 2px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 24px;
      width: 24px;
      height: 24px;
      line-height: 1;
      &:hover { color: #fff; opacity: 0.8; }
    }
    .badge {
      position: absolute;
      top: -2px;
      left: 9px;
      width: 15px;
      height: 15px;
      padding: 2px;
      box-sizing: border-box;
      background: #FF3B30;
      color: #fff;
      font-size: 0.55rem;
      font-weight: 700;
      line-height: 1;
      border-radius: 100px;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      pointer-events: none;
    }
  `],
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  readonly store = inject(NotificationInboxStore);
  private readonly pusher = inject(PusherService);
  private readonly elementRef = inject(ElementRef);

  private subs = new Subscription();
  private fallbackTimer?: ReturnType<typeof setInterval>;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.store.closePanel();
    }
  }

  ngOnInit() {
    this.store.pollUnreadCount();

    // Bump the badge on every inbox write, independent of the recipient's
    // browser_push/mobile_push/email preferences — `inbox` fires unconditionally
    // on the backend, unlike `browser_push` which only fires when that channel
    // is configured for the event. See handlePushNotification for why we bump
    // locally instead of re-polling the backend here.
    this.subs.add(
      this.pusher.inboxUpdate$.subscribe(() => this.store.handlePushNotification())
    );

    // Watch Pusher connection state:
    // connected  → stop fallback timer (Pusher handles updates)
    // disconnected → start fallback timer every 60s
    this.subs.add(
      this.pusher.connected$.subscribe(connected => {
        if (connected) {
          this.stopFallbackTimer();
          this.store.pollUnreadCount();
        } else {
          this.startFallbackTimer();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.stopFallbackTimer();
  }

  private startFallbackTimer() {
    if (this.fallbackTimer) return;
    this.fallbackTimer = setInterval(() => this.store.pollUnreadCount(), 60_000);
  }

  private stopFallbackTimer() {
    if (this.fallbackTimer) {
      clearInterval(this.fallbackTimer);
      this.fallbackTimer = undefined;
    }
  }
}
